/**
 * Chat change sets — the Word half of the agent's ``doc_propose_changes``.
 *
 * The agent names each edit by section + the exact text it changes (a
 * quote), never by paragraph index: every change is located afresh just
 * before it is applied, so an earlier change in the set (or the writer
 * typing) can't shift it onto the wrong paragraph. Each change is applied
 * on its own — one failure is reported and the rest still land.
 *
 * Every applied change carries its inverse (``undo``), so the chat's
 * Undo / Redo can reverse a set and re-apply it through this same path
 * (Office.js can't drive Word's own Undo stack).
 */

import { needsFormatterRender } from "./formatter";
import {
  deleteParagraphsInSection,
  insertMarkdownNearQuote,
  locateQuoteInSection,
  replaceTextInSection,
  replaceTextInSectionStyled,
  type SectionSpanReplaceResult,
} from "./wordWriter";

export type ChangeOp = "replace" | "delete" | "insert_after" | "insert_before";

export interface ChangeSpec {
  id: string;
  section: string;
  op: ChangeOp;
  quote: string;
  new_text?: string;
}

export interface ChangeLocateResult {
  id: string;
  status: "found" | "not_found" | "ambiguous" | "table_conflict" | "error";
  /** The located text as the document reads it. */
  before?: string;
  error?: string;
}

export interface ChangeApplyResult {
  id: string;
  success: boolean;
  error?: string;
  /** The text that now stands in the document ("" for a delete). */
  after?: string;
  /** The change that reverses this one; null when it can't be reversed. */
  undo?: ChangeSpec | null;
}

/** Plain text a markdown block reads as once rendered — what Word will
 *  hold, so a later locate (jump-to, Undo) can find it. */
export function markdownPlain(markdown: string): string {
  return markdown
    .split("\n")
    .map((line) =>
      line
        .trim()
        .replace(/^#{1,6}\s+/, "")
        .replace(/^(?:[-*•]|\d+[.)])\s+/, "")
        .replace(/\*\*|__/g, ""),
    )
    .filter((line) => line.length > 0)
    .join("\n");
}

const _touchesInsertOnly = (op: ChangeOp) => op === "insert_after" || op === "insert_before";

/** Locate every change's quote in its section; nothing is written. */
export async function locateChanges(changes: ChangeSpec[]): Promise<ChangeLocateResult[]> {
  const out: ChangeLocateResult[] = [];
  for (const change of changes) {
    try {
      const loc = await locateQuoteInSection(change.section, change.quote);
      if (loc.status !== "found") {
        out.push({ id: change.id, status: loc.status, error: loc.error ?? loc.status });
      } else if (loc.inTable && !_touchesInsertOnly(change.op)) {
        out.push({ id: change.id, status: "table_conflict", error: "table_conflict" });
      } else {
        out.push({ id: change.id, status: "found", before: loc.text });
      }
    } catch (err) {
      out.push({ id: change.id, status: "error", error: err instanceof Error ? err.message : String(err) });
    }
  }
  return out;
}

const _replaceFailed = (r: SectionSpanReplaceResult) => r === "not_found" || r === "table_conflict";

async function _replace(section: string, quote: string, text: string): Promise<SectionSpanReplaceResult> {
  return needsFormatterRender(text)
    ? replaceTextInSectionStyled(section, quote, text)
    : replaceTextInSection(section, quote, text);
}

/** Apply one change, returning its outcome and inverse. Never throws. */
export async function applyChange(change: ChangeSpec): Promise<ChangeApplyResult> {
  const { id, section, op, quote } = change;
  const newText = (change.new_text ?? "").trim();
  try {
    const loc = await locateQuoteInSection(section, quote);
    if (loc.status !== "found") return { id, success: false, error: loc.error ?? loc.status };

    if (op === "replace") {
      const r = await _replace(section, quote, newText);
      if (_replaceFailed(r)) return { id, success: false, error: r };
      const after = markdownPlain(newText);
      return { id, success: true, after, undo: { id, section, op: "replace", quote: after, new_text: loc.text } };
    }

    if (op === "delete") {
      if (loc.wholeParagraphs) {
        const r = await deleteParagraphsInSection(section, quote);
        if (r !== "deleted") return { id, success: false, error: r };
        const restored = loc.paragraphs.join("\n\n");
        const undo: ChangeSpec | null = loc.prevParagraph
          ? { id, section, op: "insert_after", quote: loc.prevParagraph, new_text: restored }
          : loc.nextParagraph
            ? { id, section, op: "insert_before", quote: loc.nextParagraph, new_text: restored }
            : null;
        return { id, success: true, after: "", undo };
      }
      // Part of a paragraph: cut the quote out; the inverse swaps the
      // trimmed paragraph(s) back for the originals.
      const r = await replaceTextInSection(section, quote, "");
      if (_replaceFailed(r)) return { id, success: false, error: r };
      const remainder = loc.remainder.join("\n");
      return {
        id,
        success: true,
        after: remainder,
        undo: remainder
          ? { id, section, op: "replace", quote: remainder, new_text: loc.paragraphs.join("\n") }
          : null,
      };
    }

    // insert_after / insert_before
    if (!newText) return { id, success: false, error: "empty_insert" };
    const r = await insertMarkdownNearQuote(section, quote, newText, op === "insert_after" ? "after" : "before");
    if (r !== "inserted") return { id, success: false, error: r };
    const after = markdownPlain(newText);
    return { id, success: true, after, undo: { id, section, op: "delete", quote: after } };
  } catch (err) {
    return { id, success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/** Apply a set, each change on its own, in order. */
export async function applyChanges(changes: ChangeSpec[]): Promise<ChangeApplyResult[]> {
  const out: ChangeApplyResult[] = [];
  for (const change of changes) out.push(await applyChange(change));
  return out;
}
